import type { ReactNode } from 'react';

/** A reorderable list of editable items with add / remove. */
export function ArrayField<T>({
  label,
  items,
  onChange,
  blank,
  addLabel = 'Add',
  render,
  hint,
}: {
  label: string;
  items: T[];
  onChange: (next: T[]) => void;
  blank: () => T;
  addLabel?: string;
  render: (item: T, set: (next: T) => void, index: number) => ReactNode;
  hint?: string;
}) {
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label fw-semibold m-0">{label}</label>
        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => onChange([...items, blank()])}>
          {addLabel}
        </button>
      </div>
      {hint && <div className="small text-muted mb-2">{hint}</div>}
      {items.length === 0 && <div className="small text-muted fst-italic">None yet.</div>}
      {items.map((item, i) => (
        <div key={i} className="border rounded p-3 mb-2 bg-white">
          {render(item, (next) => onChange(items.map((x, k) => (k === i ? next : x))), i)}
          <div className="d-flex gap-2 mt-1">
            <button type="button" className="btn btn-sm btn-light" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
            <button type="button" className="btn btn-sm btn-light" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
            <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => onChange(items.filter((_, k) => k !== i))}>
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
